import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { HelpersService } from 'src/app/services/helpers.service';
import { JobcardService } from 'src/app/services/jobcard.service';

@Component({
  selector: 'app-workshop-jobcard-internal-external-notes',
  templateUrl: './workshop-jobcard-internal-external-notes.component.html',
  styleUrls: ['./workshop-jobcard-internal-external-notes.component.scss']
})
export class WorkshopJobcardInternalExternalNotesComponent {

  @Input() jobcard: any;
  @Input() selectedNoteType: string = 'INTERNAL';
  @Input() internalNotes: any;
  @Input() externalNotes: any;
  @Input() clientNotes: any;
  @Output() jobcardChange = new EventEmitter<any>();

  countryData: any = this.helpersService.getCountryData();
  editableNote: any;
  updatingNote: boolean = false;

  @ViewChild('noteInput') noteInput: ElementRef;

  constructor(
    public activeModal: NgbActiveModal,
    private translate: TranslateService,
    private helpersService: HelpersService,
    private jobcardService: JobcardService
  ) { }

  ngOnInit() {
    this.createEmptyState();
    this.getJobCardNotes();
    this.setNewNote();    
  }

  setNewNote() {
    this.editableNote = {
      note: '',
      newNote: true,
      noteType: this.selectedNoteType,
      createdDate: new Date().toISOString(),
    }
    setTimeout(()=>{
      this.noteInput.nativeElement.focus();
    },100);    
  }

  selectNoteType(type: string) {
    this.selectedNoteType = type;
    this.setNewNote();
  }

  editNote(note: any) {
    this.editableNote = { ...note, newNote: false, originalNote: note.note };
  }

  createEmptyState() {
    if (!this.internalNotes) {
      this.internalNotes = {
        serviceID: this.jobcard.id,
        noteType: 'INTERNAL',
        notes: []
      };
    }
    if (!this.externalNotes) {
      this.externalNotes = {
        serviceID: this.jobcard.id,
        noteType: 'EXTERNAL',
        notes: []
      };
    }
    if (!this.clientNotes) {
      this.clientNotes = {
        serviceID: this.jobcard.id,
        noteType: 'CLIENTNOTES',
        notes: []
      };
    }
  }

  getJobCardNotes() {
    this.jobcardService.getServiceNotes(this.jobcard.partnerID, this.jobcard.id).subscribe(
      (notes) => {
        var internalNotes = notes.filter(note => note.noteType === 'INTERNAL').pop();
        var externalNotes = notes.filter(note => note.noteType === 'EXTERNAL').pop();
        var clientNotes = notes.filter(note => note.noteType === 'CLIENTNOTES').pop();

        if (internalNotes) {
          this.internalNotes = internalNotes;
        }else{
          this.internalNotes = {
            serviceID: this.jobcard.id,
            noteType: 'INTERNAL',
            notes: []
          };
        }
        if (externalNotes) {
          this.externalNotes = externalNotes;
        }else{
          this.externalNotes = {
            serviceID: this.jobcard.id,
            noteType: 'EXTERNAL',
            notes: []
          };
        }
        if (clientNotes) {
          this.clientNotes = clientNotes;          
        }else{
          this.clientNotes = {
            serviceID: this.jobcard.id,
            noteType: 'CLIENTNOTES',
            notes: []
          };
        }

        if (notes && notes.length > 0) {
          const allNotes = notes.flatMap(group => group.notes);
          const mostRecentNote = allNotes.reduce((latest, current) => {
            return new Date(current.createdDate) > new Date(latest.createdDate) ? current : latest;
          });
          if (mostRecentNote) {
            this.jobcard.latestNote = mostRecentNote;
          }else{
            this.jobcard.latestNote = null;
          }
        }else{
          this.jobcard.latestNote = null;
        }

      },
      (error) => {

      }
    );
  }

  saveNote() {
    var newNote = {
      note: this.editableNote.note,
      noteType: this.selectedNoteType,
      serviceID: this.jobcard.id,
      partnerID: this.jobcard.partnerID,
    }
    this.updatingNote = true;
    this.jobcardService.addUpdateServiceNotes(newNote).subscribe(result => {
      this.getJobCardNotes();
      this.setNewNote();
      this.updatingNote = false;
    }, err => {
      // Handle error
      this.updatingNote = false;
    });
  }

  updateNote() {
    var update = {
      id: this.editableNote.id,
      note: this.editableNote.note,
      noteType: this.editableNote.noteType,
      serviceID: this.jobcard.id,
      partnerID: this.jobcard.partnerID,
    }
    this.updatingNote = true;
    this.jobcardService.addUpdateServiceNotes(update).subscribe(result => {
      this.getJobCardNotes();
      this.setNewNote();
      this.updatingNote = false;
    }, err => {
      // Handle error
      this.updatingNote = false;
    });

  }

  deleteNote() {
    this.updatingNote = true;
    this.jobcardService.deleteServiceNote(this.jobcard.partnerID, this.jobcard.id, this.editableNote.id).subscribe(result => {
      this.getJobCardNotes();
      this.setNewNote();
      this.updatingNote = false;
    }, err => {
      this.updatingNote = false;
    });
  }

  getCurrentNotes() {
    const notes = this.selectedNoteType === 'INTERNAL' ? this.internalNotes?.notes : this.selectedNoteType === 'EXTERNAL' ? this.externalNotes?.notes : this.selectedNoteType === 'CLIENTNOTES' ? this.clientNotes?.notes : [];
    return notes || [];
  }

  // Add this method to handle sorting
  getSortedNotes() {
    return this.getCurrentNotes().sort((a, b) =>
      new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
    );
  }

  shouldShowDateSeparator(note: any, index: number): boolean {
    if (index === 0) return true; // Always show for first note

    const sortedNotes = this.getSortedNotes();
    const currentDate = new Date(note.createdDate).toDateString();
    const previousDate = new Date(sortedNotes[index - 1].createdDate).toDateString();

    return currentDate !== previousDate;
  }

  getDateDisplayText(dateString: string): string {
    const noteDate = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (noteDate.toDateString() === today.toDateString()) {
      return this.translate.instant('bikeshop.workshop.jobcard.manage.messaging.today');
    } else if (noteDate.toDateString() === yesterday.toDateString()) {
      return this.translate.instant('bikeshop.workshop.jobcard.manage.messaging.yesterday');
    } else {
      return this.translate.instant('bikeshop.workshop.jobcard.manage.messaging.older');
    }
  }

}
